import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Drawer, DrawerContent, Spinner } from '@heroui/react'
import { useAppSelector } from '@/redux/hooks'
import { useStoreMenu } from '@/hooks/use-store-menu'
import { useMediaQuery } from '@/hooks/use-media-query'
import { MisakoHeader } from '@/ui/custom/misako-header'
import { NavArrowLeftIcon } from '@/ui/icons/nav-arrow-left'
import { CreditFooter } from './credit-footer'
import { StoreProducts } from './products/store-products'
import { StoreMenu } from './store-menu'
import { StoreSearch } from './store-search'
import { CartButton } from './cart/cart-button'
import { Cart } from './cart/cart'
import { ProductDetailsDrawer } from './product-details/product-details-drawer'
import {
  DEFAULT_CATEGORY_INDEX,
  DEFAULT_COUNTRY_CODE
} from './helpers/constants'

export type Filters = {
  search: string
  categoryIndex: number | null
}

interface FullCatalogProps {
  currentReturnedItemId: string
  onContinue: () => void
}

export const DesktopFullCatalog = ({
  currentReturnedItemId,
  onContinue
}: FullCatalogProps) => {
  const { i18n, t } = useTranslation()

  const isMobile = useMediaQuery('(max-width:599px)')

  const [filters, setFilters] = useState<Filters>({
    search: '',
    categoryIndex: DEFAULT_CATEGORY_INDEX
  })

  const [isCartOpen, setIsCartOpen] = useState(false)

  const onChangeFilters = (filters: Filters) => {
    setFilters(filters)

    if (isCartOpen) {
      setIsCartOpen(false)
    }
  }

  const onOpenCart = (open: boolean) => {
    setIsCartOpen(open)

    setFilters({
      search: '',
      categoryIndex: open ? null : DEFAULT_CATEGORY_INDEX
    })
  }

  const [searchParams, setSearchParams] = useSearchParams()

  const fromProductId = searchParams.get('from_product_id')

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const countryCode =
    useAppSelector((store) => store.return.returnAddress.country_code) ??
    DEFAULT_COUNTRY_CODE

  const orderReference = useAppSelector(
    (store) =>
      store.returnsApi.order.response?.order?.identification
        ?.customer_printed_order_id
  )

  const { data: menu, status: menuStatus } = useStoreMenu({
    ecommerceId,
    language: i18n.language,
    countryCode,
    orderReference
  })

  const selectedCategory =
    filters.categoryIndex !== null
      ? menu?.items?.[filters.categoryIndex]
      : undefined

  const onOpenProductDetails = (open: boolean) => {
    if (open && fromProductId) {
      searchParams.set('from_product_id', fromProductId)
      setSearchParams(searchParams, { replace: true })

      return
    }

    searchParams.delete('from_product_id')
    setSearchParams(searchParams, { replace: true })
  }

  if (menuStatus === 'pending') {
    return (
      <div className="fixed inset-x-0 bottom-0 top-20 z-10 bg-[#f2f2f2]">
        <div className="flex size-full items-center justify-center">
          <Spinner
            size="lg"
            color="current"
            classNames={{
              circle1: 'border-[3px] text-primary-main',
              circle2: 'border-[3px] text-primary-main'
            }}
          />
        </div>
      </div>
    )
  }

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (isHoff) {
    return (
      <>
        <div className="fixed inset-x-0 bottom-0 top-14 z-10 overflow-hidden bg-white px-4 md:px-8">
          <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
            <div className="sticky top-0 z-10 bg-white pb-4 pt-6">
              <button
                onClick={onContinue}
                aria-label={t('full_catalog.back')}
                className="-ml-1 flex items-center gap-1 text-sm uppercase tracking-wider text-neutral-900"
              >
                <NavArrowLeftIcon />
                {t('full_catalog.back')}
              </button>
              <div className="flex items-center justify-between gap-4 pt-6">
                {menu ? (
                  <StoreMenu
                    categories={menu.items}
                    categoryIndex={filters.categoryIndex}
                    onChangeFilters={onChangeFilters}
                  />
                ) : null}
                <div className="flex items-center gap-4">
                  <StoreSearch
                    search={filters.search}
                    onChangeFilters={onChangeFilters}
                  />
                  <CartButton onOpenCart={onOpenCart} />
                </div>
              </div>
              <h2 className="pt-4 text-[1.75rem] font-medium leading-normal">
                {isCartOpen
                  ? t('full_catalog.your_selected_items')
                  : filters.search ||
                    (selectedCategory?.title ? selectedCategory.title : null)}
              </h2>
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
              {isCartOpen ? (
                <Cart
                  currentReturnedItemId={currentReturnedItemId}
                  onClose={() => onOpenCart(false)}
                />
              ) : (
                <StoreProducts
                  filters={filters}
                  selectedCategory={selectedCategory}
                  returnedItemId={currentReturnedItemId}
                />
              )}
            </div>
            <div className="mx-auto w-fit bg-transparent">
              <CreditFooter onContinue={onContinue} />
            </div>
          </div>
        </div>
        <Drawer
          isOpen={!!fromProductId}
          onOpenChange={onOpenProductDetails}
          placement="bottom"
          size="xl"
          classNames={{
            base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
            closeButton: 'z-10'
          }}
        >
          <DrawerContent>
            {fromProductId ? (
              <ProductDetailsDrawer
                productId={fromProductId}
                onClose={() => onOpenProductDetails(false)}
                returnedItemId={currentReturnedItemId}
              />
            ) : null}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  if (isMisako) {
    return (
      <>
        <div className="fixed inset-0 z-10 bg-white">
          <div className="relative">
            <MisakoHeader />
            <div className="px-6 pt-6">
              <div className="space-y-3 pb-6">
                <div className="flex items-center justify-between gap-4">
                  <button
                    onClick={onContinue}
                    className="flex cursor-pointer items-center gap-2 font-light uppercase tracking-wider underline-offset-8 hover:underline"
                  >
                    <NavArrowLeftIcon />
                    Volver
                  </button>
                  {menu ? (
                    <StoreMenu
                      categories={menu.items}
                      categoryIndex={filters.categoryIndex}
                      onChangeFilters={onChangeFilters}
                    />
                  ) : null}
                  <CartButton onOpenCart={onOpenCart} />
                </div>
                <StoreSearch
                  search={filters.search}
                  onChangeFilters={onChangeFilters}
                />
              </div>
              <div className="relative flex min-h-full flex-col overflow-y-auto">
                {isCartOpen ? (
                  <Cart
                    currentReturnedItemId={currentReturnedItemId}
                    onClose={() => onOpenCart(false)}
                  />
                ) : (
                  <StoreProducts
                    filters={filters}
                    selectedCategory={selectedCategory}
                    returnedItemId={currentReturnedItemId}
                  />
                )}
              </div>
            </div>
            <div className="sticky bottom-0 mx-auto w-fit bg-transparent">
              <CreditFooter onContinue={onContinue} />
            </div>
          </div>
        </div>
        <Drawer
          isOpen={!!fromProductId}
          onOpenChange={onOpenProductDetails}
          placement="bottom"
          size="xl"
          classNames={{
            base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
            closeButton: 'z-10'
          }}
        >
          <DrawerContent>
            {fromProductId ? (
              <ProductDetailsDrawer
                productId={fromProductId}
                onClose={() => onOpenProductDetails(false)}
                returnedItemId={currentReturnedItemId}
              />
            ) : null}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 top-20 z-10 grid bg-[rgb(242,242,242)] [&:has(>:nth-child(2))]:grid-cols-[auto,1fr]">
        {menu && !isMobile ? (
          <StoreMenu
            categories={menu.items}
            categoryIndex={filters.categoryIndex}
            onChangeFilters={onChangeFilters}
          />
        ) : null}
        <div className="relative flex min-h-full flex-col overflow-y-scroll px-2 md:px-4">
          <div className="sticky top-0 z-50 w-full bg-[#f2f2f2]">
            <div className="pb-3 pt-4">
              <div className="flex items-center justify-between gap-4 pb-3">
                <StoreSearch
                  search={filters.search}
                  onChangeFilters={onChangeFilters}
                />
                <CartButton onOpenCart={onOpenCart} />
              </div>
              {menu && isMobile ? (
                <StoreMenu
                  categories={menu.items}
                  categoryIndex={filters.categoryIndex}
                  onChangeFilters={onChangeFilters}
                />
              ) : null}
              {isMobile ? (
                isCartOpen ? (
                  <h2 className="pt-2 text-lg font-medium leading-normal md:text-[1.75rem]">
                    {t('full_catalog.your_selected_items')}
                  </h2>
                ) : null
              ) : (
                <h2 className="pt-2 text-lg font-medium leading-normal md:text-[1.75rem]">
                  {isCartOpen
                    ? t('full_catalog.your_selected_items')
                    : filters.search ||
                      (selectedCategory?.title ? selectedCategory.title : null)}
                </h2>
              )}
            </div>
          </div>
          {isCartOpen ? (
            <Cart
              currentReturnedItemId={currentReturnedItemId}
              onClose={() => onOpenCart(false)}
            />
          ) : (
            <StoreProducts
              filters={filters}
              selectedCategory={selectedCategory}
              returnedItemId={currentReturnedItemId}
            />
          )}
          <div className="sticky bottom-0 z-50 mx-auto mt-auto w-fit bg-transparent">
            <CreditFooter onContinue={onContinue} />
          </div>
        </div>
      </div>
      <Drawer
        isOpen={!!fromProductId}
        onOpenChange={onOpenProductDetails}
        placement="bottom"
        size="xl"
        classNames={{
          base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
          closeButton: 'z-10'
        }}
      >
        <DrawerContent>
          {fromProductId ? (
            <ProductDetailsDrawer
              productId={fromProductId}
              onClose={() => onOpenProductDetails(false)}
              returnedItemId={currentReturnedItemId}
            />
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  )
}
